/**
 * `<PersistentRightPanel/>` — the lead's right panel: NEXT STEPS (#1921).
 *
 * Mounts on `/teams/:tid*` and `/cycles/:cid*` (the path decision lives in
 * `lib/persistentPanel.ts`); AppShell's `<PanelAwareLayout/>` renders it
 * inside the `ui/SideDrawer` overlay its handle opens — closed by default,
 * 360 px, over the page's right edge (#1920). The drawer is the frame, the
 * title (*Next steps*) and the scroll; this container carries only its gap
 * and padding.
 *
 * **Why the lead opens it:** to know what still needs them on this roster
 * without reading the grid. **What they see first:** the one most urgent
 * thing, as a sentence with a count. **What they do straight away:** click
 * the row and land where the action is. (Handover R3,
 * `docs/agent-prompts/handover-right-panel-and-inbox-2026-10-02.md` §3.)
 *
 * **Content, in order:**
 *
 *   - `<NextStepsSection/>` — one row per kind of step, fixed urgency order:
 *     days short on published days (with validation's *N issues to review*
 *     folded in as a second link) · leave requests to review · swaps that
 *     need your confirm · unpublished edits · the team not told · pins that
 *     can't be placed · (team pages) imported calls waiting for a period /
 *     no schedules yet · submissions outstanding (only for a period that
 *     collects them). Empty: *Nothing needs you on this roster.* The
 *     handle's badge is its row count (`lib/persistentPanelBadge.ts`).
 *   - `<TeamAuditTickerSection limit={3}/>` — *Recent activity* as a calm
 *     three-row tail with *See full activity →*, gated on `audit_retention`
 *     as before.
 *
 * **What left the panel (#1921).** The status sections — Submissions,
 * Validation, Coverage — became next-step rows or a row's second line; the
 * Re-balance, Retrospective, Live mood, Forecast and Workflow pills are
 * reads, not steps, so they left the panel. Their components (and the
 * hooks the next-steps list reads from them) stay: their pages keep them —
 * the Hours chip and modal, the team page's insights bar, the roster's
 * validation and coverage sections, the toolbar's Submissions.
 *
 * **History.** From #194 (IA-rework PR 1) to #1920 this was an always-open
 * 330 px column of status sections in the order of the IA-rework research
 * §7.6 (`docs/research/ui-presentation-ia-rework-2026-05-09.md`): the
 * forward-looking trio (Submissions → Validation → Coverage → Re-balance)
 * above the retrospective audit ticker, then the toggleable pills
 * (Retrospective, Live mood, Forecast, Workflows), each behind its
 * `<ComponentGate>`; the privacy strip left it in #208 and the credit meter
 * in #1294. That research's item 5 — a "What's next" smart suggestion,
 * deferred then — is what this panel now is.
 *
 * **Brand-pillar discipline.** One focal element — the first (most urgent)
 * row; the rest of the panel stays receded so the page keeps the lead's
 * attention.
 */

import { useResolvedTeamId } from "../lib/useResolvedTeamId";
import { ComponentGate } from "./ComponentGate";
import { TeamAuditTickerSection } from "./TeamAuditTickerSection";
import { NextStepsSection } from "./panel/NextStepsSection";

/** Rows in the *Recent activity* tail under the steps. */
const RECENT_ACTIVITY_ROWS = 3;

export function PersistentRightPanel() {
  // Panel-level teamId resolution drives the `audit_retention` gate on the
  // activity tail (#193). The underlying `useCycle` cache is shared, so
  // this does not double-fetch.
  const { teamId } = useResolvedTeamId();

  return (
    <aside
      aria-label="Team context panel"
      data-testid="persistent-right-panel"
      data-variant="lead"
      className="flex flex-col gap-4 p-3"
    >
      <NextStepsSection />
      {/* The quiet tail: what just happened, three rows. Gated on
          `audit_retention` (registry §154); the section also self-hides for
          non-leads and on a fetch error. */}
      {teamId ? (
        <ComponentGate component="audit_retention" teamId={teamId} ifOff="hide">
          <TeamAuditTickerSection limit={RECENT_ACTIVITY_ROWS} />
        </ComponentGate>
      ) : (
        <TeamAuditTickerSection limit={RECENT_ACTIVITY_ROWS} />
      )}
    </aside>
  );
}
